// utils/validation.ts
import { BuilderField, ValidationError, OPTION_TYPES } from '../types/BuilderField';

export interface ValidateFieldOptions {
  existingFields: BuilderField[];
  editingFieldId?: BuilderField['id'];
}

/**
 * Validates a single field before create/update:
 * - fieldKey unique (duplicate labels are fine, duplicate keys are not)
 * - label required
 * - orderNo > 0
 * - category required
 * - select/multiselect must have at least one option
 * - max 2 fields may share the same orderNo within a category
 */
export function validateField(
  field: BuilderField,
  { existingFields, editingFieldId }: ValidateFieldOptions
): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!field.label?.trim()) {
    errors.push({ message: 'Label cannot be empty.' });
  }

  if (!field.fieldKey?.trim()) {
    errors.push({ message: 'Field key is required.' });
  } else {
    const duplicateKey = existingFields.some(
      (f) => f.fieldKey === field.fieldKey && f.id !== editingFieldId
    );
    if (duplicateKey) {
      errors.push({ message: 'Field key cannot duplicate an existing field.' });
    }
  }

  if (!field.category?.trim()) {
    errors.push({ message: 'Category is required.' });
  }

  if (!field.orderNo || field.orderNo <= 0) {
    errors.push({ message: 'Order must be greater than zero.' });
  }

  if (OPTION_TYPES.includes(field.fieldType)) {
    if (!field.options || field.options.length === 0) {
      errors.push({ message: 'Select fields must contain at least one option.' });
    }
  }

  if (field.category && field.orderNo) {
    const sameOrderCount = existingFields.filter(
      (f) =>
        f.category === field.category &&
        f.orderNo === field.orderNo &&
        f.id !== editingFieldId
    ).length;

    if (sameOrderCount + 1 > 2) {
      errors.push({ message: 'Maximum 2 fields may share the same Order Number.' });
    }
  }

  return errors;
}

/** Suggests the next available orderNo in a category (end of the last row, +1). */
export function nextOrderNoForCategory(fields: BuilderField[], category: string): number {
  const inCategory = fields.filter((f) => f.category === category);
  if (inCategory.length === 0) return 1;
  return Math.max(...inCategory.map((f) => f.orderNo)) + 1;
}